import {
  CONDITION_LABELS,
  ITEM_CONDITIONS,
  type Item,
  type ItemCondition,
  type ReviewAction,
  type ReviewChoice,
  type ReviewRow,
} from "@iltocat/shared";
import { Icon } from "./Icon.js";
import { Segmented } from "@vcnet/ui";

/**
 * One item's proposed changes, each with Accept, Edit and Ignore.
 *
 * Nothing here saves. It draws rows and reports what somebody chose; the
 * screen above decides what to send and when. **Edit** is not a modal: it opens
 * the proposed value in an input right there, and typing into it is itself an
 * acceptance -- "I changed it, so I want it".
 *
 * A row that would fill a blank starts on Accept and one that would replace
 * something already written starts on Ignore (that default is `reviewRows()`'s,
 * not this file's), and the current value is always shown beside the proposal
 * so the choice is a comparison rather than a leap.
 */

const ACTIONS: { action: ReviewAction; label: string }[] = [
  { action: "accept", label: "Accept" },
  { action: "edit", label: "Edit" },
  { action: "ignore", label: "Ignore" },
];

const shown = (row: ReviewRow, value: string): string =>
  row.kind === "condition" ? (CONDITION_LABELS[value as ItemCondition] ?? value) : value;

export function EnrichReview({
  item,
  rows,
  choices,
  onChoose,
  disabled,
}: {
  item: Item;
  rows: ReviewRow[];
  choices: Record<string, ReviewChoice>;
  onChoose: (key: string, choice: ReviewChoice) => void;
  disabled: boolean;
}) {
  if (rows.length === 0) {
    return <p className="muted">Nothing to change. The reply agrees with what is already there.</p>;
  }

  return (
    <ul className="review-rows">
      {rows.map((row) => {
        const choice = choices[row.key] ?? { action: row.action, value: row.proposed };
        const set = (action: ReviewAction, value = choice.value) => onChoose(row.key, { action, value });
        const private_ =
          row.kind === "field" &&
          item.fields.some(
            (field) =>
              field.visibility === "private" && field.label.toLowerCase() === row.label.toLowerCase(),
          );

        return (
          <li key={row.key} className={`review-row review-${choice.action}`}>
            <div className="review-head">
              <span className="field-label">
                {row.label}
                {private_ && (
                  <span className="pill">
                    <Icon name="lock" size={12} />
                    Private
                  </span>
                )}
              </span>
              <Segmented<ReviewAction>
                label={`What to do with ${row.label}`}
                size="sm"
                value={choice.action}
                disabled={disabled}
                onChange={(action) => set(action)}
                options={ACTIONS.map(({ action, label }) => ({ value: action, label }))}
              />
            </div>

            <p className="review-was">
              <span className="review-key">Now</span>
              {row.current ? shown(row, row.current) : <span className="muted">Empty</span>}
            </p>

            {choice.action === "edit" ? (
              row.kind === "description" ? (
                <textarea
                  className="input input-area"
                  rows={3}
                  disabled={disabled}
                  aria-label={`${row.label}, edited`}
                  value={choice.value}
                  onChange={(event) => set("edit", event.target.value)}
                />
              ) : row.kind === "condition" ? (
                <select
                  className="input"
                  disabled={disabled}
                  aria-label={`${row.label}, edited`}
                  value={choice.value}
                  onChange={(event) => set("edit", event.target.value)}
                >
                  {ITEM_CONDITIONS.filter(Boolean).map((condition) => (
                    <option key={condition} value={condition}>
                      {CONDITION_LABELS[condition]}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  disabled={disabled}
                  aria-label={`${row.label}, edited`}
                  value={choice.value}
                  onChange={(event) => set("edit", event.target.value)}
                />
              )
            ) : (
              <p className="review-new">
                <span className="review-key">New</span>
                {shown(row, choice.value)}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
